import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { Link } from 'react-router-dom';

import { sendTwoFactorEmail, SignInRefused } from '../api/client';
import type { TwoFactorMethod, TwoFactorStep } from '../api/client';
import { ApiProblem, TWO_FACTOR_EXPIRED } from '../api/problem';
import { PUBLIC_ROUTES } from '../routes';
import { useSession } from '../session/useSession';
import { AuthShell, PasswordInput } from './auth/AuthShell';
import { CheckedForm, Field, LIMITS } from './form';
import { Icon } from './Icon';
import { CodeInput } from './twofactor/CodeInput';

export function SignInPage(): ReactNode {
    const { t, signIn, notice } = useSession();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<{ readonly tone: 'info' | 'warn' | 'danger'; readonly text: string } | null>(null);
    const [step, setStep] = useState<TwoFactorStep | null>(null);

    async function submit(): Promise<void> {
        setPending(true);
        setError(null);

        try {
            const next = await signIn(email, password);

            if (next !== null) {
                setStep(next);
                setPending(false);
            }
        } catch (cause) {
            // Le serveur répond la même chose pour une adresse inconnue, un mot
            // de passe faux, un compte verrouillé ou une population à qui le mot
            // de passe est interdit — sans quoi le formulaire deviendrait un
            // oracle d'énumération de comptes. Le front n'a donc rien de plus
            // précis à dire, et n'a pas à inventer.
            setError(cause instanceof SignInRefused ? { tone: 'danger', text: t('signIn.refusedLong') } : { tone: 'warn', text: t('signIn.unreachableLong') });
            setPending(false);
        }
    }

    if (step !== null) {
        return (
            <AuthShell>
                <SecondFactor
                    step={step}
                    onRestart={(message) => {
                        setStep(null);
                        setPassword('');
                        setError(message === null ? null : { tone: 'info', text: message });
                    }}
                />
            </AuthShell>
        );
    }

    const notices = { passwordChanged: 'signIn.passwordChanged', passwordSet: 'signIn.passwordSet', sessionExpired: 'session.expiredLong' } as const;

    return (
        <AuthShell>
            <CheckedForm className="auth-form" onSubmit={() => void submit()}>
                <div className="auth-form__head">
                    <h1>{t('signIn.title')}</h1>
                    <span className="muted">{t('auth.subtitle')}</span>
                </div>

                {/*
                    Pourquoi on est revenu ici, quand on y a été reconduit : après
                    un changement de mot de passe, TOUTES les sessions sont
                    coupées (D30), et l'utilisateur doit savoir que ce n'est pas
                    une panne.
                */}
                {notice === null || error !== null ? null : (
                    <AuthNotice tone={notice === 'sessionExpired' ? 'info' : 'ok'}>{t(notices[notice])}</AuthNotice>
                )}
                {error === null ? null : <AuthNotice tone={error.tone}>{error.text}</AuthNotice>}

                {/* Champs gardés à la main : `name` sert aux gestionnaires de mots de passe. */}
                <Field id="signin-email" label={t('signIn.email')} required>
                    <input
                        id="signin-email"
                        className="field__input"
                        type="email"
                        name="email"
                        autoComplete="username"
                        required
                        aria-required={true}
                        maxLength={LIMITS.UserAccount.email.max ?? undefined}
                        aria-invalid={error?.tone === 'danger' ? true : undefined}
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                    />
                </Field>

                <Field id="signin-password" label={t('signIn.password')} required>
                    <PasswordInput id="signin-password" name="password" autoComplete="current-password" invalid={error?.tone === 'danger'} value={password} onChange={setPassword} />
                </Field>

                <Link className="auth-form__forgot" to={PUBLIC_ROUTES.forgotPassword}>
                    {t('signIn.forgot')}
                </Link>

                <button className="button auth-form__submit" type="submit" disabled={pending || email.trim() === '' || password === ''}>
                    {pending ? t('signIn.pending') : t('signIn.submit')}
                </button>
            </CheckedForm>
        </AuthShell>
    );
}

/** Un avis en tête de formulaire : refus, session expirée, mot de passe changé, panne. */
export function AuthNotice({ tone, children }: { readonly tone: 'ok' | 'info' | 'warn' | 'danger'; readonly children: ReactNode }): ReactNode {
    const icon = { ok: 'check', info: 'info', warn: 'alert', danger: 'alert' }[tone];

    return (
        <div className={`auth-notice auth-notice--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
            <Icon name={icon} size={18} />
            <span>{children}</span>
        </div>
    );
}

/**
 * D39 — la seconde étape (planches 27, 28 et 30) : le code par courriel, celui de
 * l'application, ou un code de secours. Un défi mort (expiré, cinq codes faux)
 * renvoie au mot de passe avec un message.
 */
function SecondFactor({ step, onRestart }: { readonly step: TwoFactorStep; onRestart(message: string | null): void }): ReactNode {
    const { t, completeTwoFactor } = useSession();
    const [method, setMethod] = useState<TwoFactorMethod>(step.method ?? step.methods[0] ?? 'email');
    const [code, setCode] = useState('');
    const [recovery, setRecovery] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [wait, setWait] = useState(step.method === 'email' ? step.resendIn : 0);
    const [emailSent, setEmailSent] = useState(step.method === 'email');
    const [info, setInfo] = useState<string | null>(null);

    useEffect(() => {
        if (wait <= 0) {
            return undefined;
        }

        const timer = window.setTimeout(() => setWait((value) => value - 1), 1000);

        return () => window.clearTimeout(timer);
    }, [wait]);

    function failed(cause: unknown): void {
        if (cause instanceof ApiProblem && (cause.type === TWO_FACTOR_EXPIRED || cause.status === 401)) {
            onRestart(t('twoFactor.expired'));

            return;
        }

        if (cause instanceof ApiProblem && cause.status === 422) {
            const left = cause.number('attemptsLeft');
            setError(left === null ? t('twoFactor.invalid') : t('twoFactor.invalidLeft', { count: left }));

            return;
        }

        if (cause instanceof ApiProblem && cause.status === 429) {
            const resend = cause.number('resendIn');

            if (resend !== null) {
                setWait(resend);
            }

            setError(t(resend === null ? 'twoFactor.tooMany' : 'twoFactor.tooSoon'));

            return;
        }

        setError(t('signIn.unreachable'));
    }

    async function submit(value: string): Promise<void> {
        setPending(true);
        setError(null);

        try {
            await completeTwoFactor(step.challenge, method, value);
        } catch (cause) {
            failed(cause);
            setCode('');
            setPending(false);
        }
    }

    async function sendEmail(): Promise<void> {
        setError(null);
        setInfo(null);

        try {
            const sent = await sendTwoFactorEmail(step.challenge);
            setMethod('email');
            setEmailSent(true);
            setWait(sent.resendIn);
            setCode('');
            setInfo(t('twoFactor.sent', { email: sent.maskedEmail }));
        } catch (cause) {
            failed(cause);
        }
    }

    const value = method === 'recovery' ? recovery : code;
    const ready = method === 'recovery' ? recovery.trim().length >= 8 : code.length === 6;

    return (
        <form
            className="auth-form two-factor"
            onSubmit={(event) => {
                event.preventDefault();

                if (ready) {
                    void submit(value);
                }
            }}
        >
            <ol className="two-factor__steps" aria-label={t('twoFactor.progress')}>
                <li className="two-factor__step two-factor__step--done">
                    <Icon name="check" size={12} />
                    {t('twoFactor.step.password')}
                </li>
                <li className="two-factor__step two-factor__step--on" aria-current="step">
                    <span>2</span>
                    {t('twoFactor.step.code')}
                </li>
            </ol>

            <h1 className="two-factor__title">{t(method === 'totp' ? 'twoFactor.app.title' : method === 'recovery' ? 'twoFactor.recovery.title' : 'twoFactor.email.title')}</h1>
            <p className="muted two-factor__body">
                {method === 'totp'
                    ? t('twoFactor.app.body')
                    : method === 'recovery'
                      ? t('twoFactor.recovery.body')
                      : emailSent
                        ? t('twoFactor.email.body', { email: step.maskedEmail })
                        : t('twoFactor.email.notSent')}
            </p>

            {step.mustEnroll ? <AuthNotice tone="info">{t('twoFactor.mustEnroll')}</AuthNotice> : null}
            {info === null ? null : <AuthNotice tone="ok">{info}</AuthNotice>}

            {method === 'recovery' ? (
                <label className="field">
                    <span className="field__label">{t('twoFactor.recovery.label')}</span>
                    <input className="field__input two-factor__recovery" autoComplete="off" autoFocus value={recovery} onChange={(event) => setRecovery(event.target.value)} placeholder="xxxx-xxxx" />
                </label>
            ) : method === 'email' && !emailSent ? null : (
                <CodeInput value={code} onChange={setCode} label={t('twoFactor.code')} invalid={error !== null} disabled={pending} autoFocus onComplete={(full) => void submit(full)} />
            )}

            {error === null ? null : <AuthNotice tone="danger">{error}</AuthNotice>}

            {method === 'email' && !emailSent ? (
                <button className="button" type="button" onClick={() => void sendEmail()}>
                    {t('twoFactor.email.send')}
                </button>
            ) : (
                <button className="button" type="submit" disabled={pending || !ready}>
                    {pending ? t('signIn.pending') : t('twoFactor.submit')}
                </button>
            )}

            {method === 'email' && emailSent ? (
                <p className="two-factor__resend">
                    {wait > 0 ? (
                        t('twoFactor.resendIn', { time: `0:${String(wait).padStart(2, '0')}` })
                    ) : (
                        <button className="link-button" type="button" onClick={() => void sendEmail()}>
                            {t('twoFactor.resend')}
                        </button>
                    )}
                </p>
            ) : null}

            <div className="two-factor__others">
                {method !== 'totp' && step.methods.includes('totp') ? (
                    <button className="link-button two-factor__other" type="button" onClick={() => { setMethod('totp'); setError(null); setCode(''); }}>
                        <Icon name="phone" size={16} />
                        {t('twoFactor.useApp')}
                    </button>
                ) : null}
                {method !== 'email' && step.methods.includes('email') ? (
                    <button className="link-button two-factor__other" type="button" onClick={() => { setError(null); void sendEmail(); }}>
                        <Icon name="mail" size={16} />
                        {t('twoFactor.useEmail')}
                    </button>
                ) : null}
                {method !== 'recovery' && step.methods.includes('recovery') ? (
                    <button className="link-button two-factor__other" type="button" onClick={() => { setMethod('recovery'); setError(null); }}>
                        <Icon name="key" size={16} />
                        {t('twoFactor.useRecovery')}
                    </button>
                ) : null}
                <button className="link-button two-factor__other two-factor__back" type="button" onClick={() => onRestart(null)}>
                    <Icon name="arrowLeft" size={16} />
                    {t('twoFactor.back')}
                </button>
            </div>
        </form>
    );
}
